import React, { useEffect, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import { BrInput } from '@govbr-ds/react-components';
import InputMask from 'react-input-mask';
import { isFunction } from 'lodash';

const InputWithMask = React.forwardRef(({
  mask,
  unmask,
  upperCase = false,
  maintainMaskedValue = false,
  defaultValue,
  value,
  onChange,
  onFocus,
  onBlur,
  onFocusCapture,
  onMouseDown,
  onPaste,
  disabled,
  readOnly,
  inputProps
}, ref) => {

  const isEmptyOnFocusRef = useRef(true);
  const lastTransformedValueRef = useRef();
  const [maskedValue, setMaskedValue] = useState();

  const rawValue = value ?? defaultValue ?? '';

  useEffect(() => {
    if (maintainMaskedValue && rawValue !== lastTransformedValueRef.current) {
      lastTransformedValueRef.current = rawValue;
      setMaskedValue(undefined);
    }
  }, [maintainMaskedValue, rawValue]);

  const transform = (raw) => {
    if (raw == null) return raw;
    const val = unmask ? unmask(raw) : raw;
    return upperCase ? val?.toUpperCase() : val;
  };

  // -----------------------------
  // CHANGE
  // -----------------------------
  const handleChange = (e) => {
    if (readOnly) return;
    const raw = e.target.value;
    const finalValue = transform(raw);

    if (maintainMaskedValue) {
      lastTransformedValueRef.current = finalValue;
      setMaskedValue(raw);
    }

    if (isFunction(onChange)) {
      onChange(finalValue);  // sempre envia VALUE, nunca evento
    }
  };

  // -----------------------------
  // FOCUS
  // -----------------------------
  const handleFocusCapture = (e) => {
    isFunction(onFocusCapture) && onFocusCapture(e);
    isEmptyOnFocusRef.current =
      !e.target.value || transform(e.target.value) === '';
  };

  const handleFocus = (e) => {
    isFunction(onFocus) && onFocus(e);

    if (!isEmptyOnFocusRef.current) {
      const { target } = e;
      setTimeout(() => {
        target.setSelectionRange(0, target.value.length);
      }, 50);
    }
  };

  // -----------------------------
  // BLUR
  // -----------------------------
  const handleBlur = (e) => {
    isFunction(onBlur) && onBlur(e);

    const raw = e.target.value;
    const finalValue = transform(raw);

    if (finalValue === '') {
      requestAnimationFrame(() => {
        if (ref?.current) {
          ref.current.value = '';
        }
      });
    }
  };

  // -----------------------------
  // DEFAULT VALUE
  // -----------------------------
  const finalValue = upperCase ? String(rawValue).toUpperCase() : rawValue;
  const inputValue = maintainMaskedValue && maskedValue !== undefined
    ? maskedValue
    : finalValue;

  // -----------------------------
  // MASK
  // -----------------------------
  const computedMask = isFunction(mask)
    ? mask(ref?.current?.value)
    : mask;

  /**
   * Mapeamento de caracteres curinga (wildcards) permitidos nas definições de máscaras do sistema.
   * 
   * - '9' : Apenas números. Ex: CPF ('999.999.999-99') ou NUP ('99999.999999/9999-99').
   * - 'a' : Apenas letras (maiúsculas e minúsculas). Ex: Siglas ou campos puramente textuais de tamanho fixo.
   * - '*' : Alfanumérico geral (letras e números). Ex: Códigos internos ou chaves de acesso flexíveis.. o para CNPJ. Permite letras e números. É convertido para letras maiúsculas. Ex: CNPJ Alfanumérico ('cc.ccc.ccc/cccc-99').
   * - 't' : Números ou hífen. Útil para flexibilizar campos onde o hífen surge de forma variável.
   * - '?' : Números ou espaço em branco. Útil para campos de preenchimento parcial ou layouts de arquivos
   *         de tamanho fixo onde posições vazias contêm espaços.
   */
  const formatChars = {
    '9': '[0-9]',
    'a': '[A-Za-z]',
    '*': '[A-Za-z0-9]',
    't': '[0-9-]',
    '?': '[0-9 ]',
  };

  return (
    <InputMask
      mask={computedMask}
      formatChars={formatChars}
      value={inputValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onFocusCapture={handleFocusCapture}
      onMouseDown={onMouseDown}
      onPaste={onPaste}
      disabled={disabled}
      readOnly={readOnly}
    >
      {(maskedProps) => (
        <TextField
          {...maskedProps}
          {...inputProps}
          inputRef={ref}
          readOnly={readOnly}
        />
      )}
    </InputMask>
  );
});

export default InputWithMask;
